import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '@pilot-hub/api-client'
import { useOptionalMissionContext } from '../../hooks/useMissionContext'
import { EinsatzOption, normalizeEinsatzListe, parseEinsatzId, suggestNachRang } from './helpers'
import { fensterKanal, FENSTER_ID } from '../multimonitor/sync'

export type EinsatzGrund = 'user' | 'deeplink' | 'vorschlag' | 'beendet' | 'ungueltig'

export interface EinsatzHinweisInfo {
  art: 'beendet' | 'nicht-verfuegbar'
  id: number
  titel: string
}

/**
 * Guard vor einem Einsatzwechsel (Auto-Save!). Darf asynchron sein.
 * `false` zurückgeben (oder werfen) bricht den Wechsel ab. `nach` = neue ID (oder null), `von` = bisherige.
 */
export type EinsatzWechselGuard = (nach: number | null, von: number | null, grund: EinsatzGrund) => void | boolean | Promise<void | boolean>

export interface EinsatzContextValue {
  /** Kandidaten (je Modul über loader/endpoint definiert) */
  einsaetze: EinsatzOption[]
  /** gewählter Einsatz (aus der Kandidatenliste) oder null */
  einsatz: EinsatzOption | null
  /** gewählte ID als number (Konvention: number überall) oder null */
  einsatzId: number | null
  /** Bequemlichkeit für <select value>: String(id) oder '' */
  einsatzIdStr: string
  /** true bis die Kandidatenliste zum ersten Mal geladen (oder fehlgeschlagen) ist */
  loading: boolean
  /** Text, wenn die Kandidatenliste nicht geladen werden konnte (Auswahl bleibt unverändert!) */
  fehler: string | null
  /** gelber Hinweis: gewählter Einsatz beendet / nicht verfügbar. Wahl ist dann null. */
  hinweis: EinsatzHinweisInfo | null
  dismissHinweis: () => void
  /** Wahl ändern. Läuft durch die Guards; liefert false, wenn abgebrochen (Guard, gesperrt, unbekannte ID). */
  setEinsatz: (id: number | string | null) => Promise<boolean>
  /** Kandidatenliste neu laden */
  reload: () => Promise<void>
  locked: boolean
  lockedHint?: string
  allowNone: boolean
  modul: string
}

const Ctx = createContext<EinsatzContextValue | null>(null)
const GuardCtx = createContext<{ add: (g: EinsatzWechselGuard) => () => void } | null>(null)

export interface EinsatzProviderProps {
  /** Modulname für den Speicherschlüssel: `pilot-hub:<modul>:einsatz:<userId>` (fuehrung, pilot, ...) */
  modul: string
  /** Nutzer-ID; Benutzerwechsel am selben Gerät übernimmt nicht den Einsatz des Vorgängers. undefined/null = 'anon'. */
  userId?: number | string | null
  /** API-Pfad der Kandidatenliste (GET über `api`). Standard '/einsaetze/offen'. Ignoriert, wenn `loader` gesetzt ist. */
  endpoint?: string
  /** Eigener Lader (z. B. `() => missionApi.getForFlight()`); Ergebnis darf Response, Array, {einsaetze}, {items} oder {einsatz} sein. */
  loader?: () => Promise<any>
  /** Zusätzlicher Filter auf die Kandidaten (z. B. nur laufende: `e => e.laufend`). */
  filter?: (e: EinsatzOption) => boolean
  /**
   * Vorbelegung, wenn nichts gewählt/gespeichert ist:
   *  'rang' = laufende vor geplanten (Führung), 'mission' = aktiver Einsatz des Nutzers aus dem MissionProvider (Teilnahme / Kräfte-Management),
   *  'none' = keine, oder Funktion (darf z. B. einen eigenen Server-Endpunkt fragen; null = kein Vorschlag).
   * Standard 'none'. Ein Vorschlag wird NICHT gespeichert (erst die bewusste Wahl).
   */
  suggest?: 'rang' | 'mission' | 'none' | ((liste: EinsatzOption[]) => number | null | undefined | Promise<number | null | undefined>)
  /** Aktualisierung der Kandidatenliste in ms (nur sichtbarer Tab). Standard 30000, 0 = nicht pollen. */
  pollMs?: number
  /** "Kein Einsatz" wählbar? Standard true. */
  allowNone?: boolean
  /** Wahl gesperrt (z. B. Co-Pilot): setEinsatz wirkt nicht, die EinsatzWahl zeigt ein Schloss. */
  locked?: boolean
  lockedHint?: string
  /** `?einsatz=` nach dem Übernehmen in der URL stehen lassen. Standard false (Parameter wird per replace entfernt). */
  keepParam?: boolean
  children: React.ReactNode
}

interface Stored { id: number | null; ts: number }

function storageKey(modul: string, userId: unknown) {
  return `pilot-hub:${modul}:einsatz:${userId ?? 'anon'}`
}
function readStored(key: string): Stored | undefined {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return undefined
    const p = JSON.parse(raw)
    if (p && typeof p === 'object' && (p.id === null || parseEinsatzId(p.id) !== null)) return { id: p.id, ts: Number(p.ts) || 0 }
  } catch { /* defekter/gesperrter Speicher */ }
  return undefined
}
function writeStored(key: string, id: number | null) {
  try { localStorage.setItem(key, JSON.stringify({ id, ts: Date.now() })) } catch { /* Speicher blockiert */ }
}
function removeStored(key: string) {
  try { localStorage.removeItem(key) } catch { /* Speicher blockiert */ }
}

function emit(name: string, detail: object) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail }))
}

export function EinsatzProvider({
  modul, userId, endpoint = '/einsaetze/offen', loader, filter, suggest = 'none', pollMs = 30000,
  allowNone = true, locked = false, lockedHint, keepParam = false, children,
}: EinsatzProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const mission = useOptionalMissionContext()
  const key = storageKey(modul, userId)

  // ---- Anfangswahl: URL (Deep-Link) > gespeichert > (nach dem Laden) Vorschlag
  const initial = () => {
    const param = parseEinsatzId(new URLSearchParams(location.search).get('einsatz'))
    if (param !== null) return { id: param, origin: 'deeplink' as const, userNone: false }
    const st = readStored(key)
    if (st) return { id: st.id, origin: 'stored' as const, userNone: st.id === null }
    return { id: null, origin: 'suggest' as const, userNone: false }
  }
  const init0 = useRef<ReturnType<typeof initial>>()
  if (!init0.current) init0.current = initial()

  const [einsatzId, setEinsatzId] = useState<number | null>(init0.current.id)
  const [einsaetze, setEinsaetze] = useState<EinsatzOption[]>([])
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<EinsatzHinweisInfo | null>(null)

  const idRef = useRef<number | null>(init0.current.id)
  const originRef = useRef<'deeplink' | 'stored' | 'user' | 'suggest'>(init0.current.origin)
  const userNoneRef = useRef(init0.current.userNone)
  const suggestDoneRef = useRef(false)
  const seenRef = useRef(false)
  const loadedRef = useRef(false)
  const listRef = useRef<EinsatzOption[]>([])
  const titelRef = useRef(new Map<number, string>())
  const guards = useRef(new Set<EinsatzWechselGuard>())
  const changeToken = useRef(0)
  const keyRef = useRef(key)
  keyRef.current = key
  const props = useRef({ endpoint, loader, filter, suggest, locked, mission })
  props.current = { endpoint, loader, filter, suggest, locked, mission }

  const applyDirect = useCallback((id: number | null, grund: EinsatzGrund, persist: boolean) => {
    const von = idRef.current
    idRef.current = id
    setEinsatzId(id)
    if (persist) writeStored(keyRef.current, id)
    if (von !== id) emit('einsatz:changed', { von, nach: id, grund })
  }, [])

  const trySuggest = useCallback(async () => {
    if (suggestDoneRef.current || idRef.current !== null || userNoneRef.current || !loadedRef.current) return
    const s = props.current.suggest
    const liste = listRef.current
    let id: number | null | undefined = null
    if (s === 'rang') id = suggestNachRang(liste)
    else if (s === 'mission') {
      const m = props.current.mission
      if (!m || m.loading) return // MissionProvider lädt noch: später erneut (Effekt unten)
      id = parseEinsatzId(m.activeMission?.id)
    } else if (typeof s === 'function') {
      try { id = await s(liste) } catch { id = null }
    }
    // ohne Treffer bei jedem Listenabruf erneut versuchen (ein Einsatz kann später entstehen/zugeordnet werden)
    if (id != null || s === 'none') suggestDoneRef.current = true
    // inzwischen anders gewählt/Liste geändert?
    if (idRef.current !== null || userNoneRef.current) return
    if (id != null && listRef.current.some(x => x.id === id)) {
      originRef.current = 'suggest'
      seenRef.current = true
      applyDirect(id, 'vorschlag', false)
    }
  }, [applyDirect])

  // ---- Kandidatenliste laden
  const inFlight = useRef<Promise<void> | null>(null)
  const reload = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current
    const run = async () => {
      let data: any
      try {
        const p = props.current
        data = p.loader ? await p.loader() : await api.get(p.endpoint)
      } catch {
        // Netzwerkfehler/5xx ist NICHT "Einsatz beendet": Wahl und Liste bleiben unverändert
        setFehler('Einsätze konnten nicht geladen werden.')
        setLoading(false)
        return
      }
      const body = data && typeof data === 'object' && 'data' in data && 'status' in data && 'headers' in data ? data.data : data
      let liste = normalizeEinsatzListe(body)
      if (props.current.filter) liste = liste.filter(props.current.filter)
      liste.forEach(e => titelRef.current.set(e.id, e.titel))
      listRef.current = liste
      loadedRef.current = true
      setEinsaetze(liste)
      setFehler(null)
      setLoading(false)

      const cur = idRef.current
      if (cur !== null) {
        if (liste.some(x => x.id === cur)) seenRef.current = true
        else {
          const titel = titelRef.current.get(cur) ?? `Einsatz ${cur}`
          if (seenRef.current) {
            // war in der Liste, ist jetzt weg -> beendet: Wahl leeren, Hinweis, KEIN automatischer Sprung
            removeStored(keyRef.current)
            suggestDoneRef.current = true
            applyDirect(null, 'beendet', false)
            setHinweis({ art: 'beendet', id: cur, titel })
          } else {
            // beim Start unbekannt (alter gespeicherter Wert / Deep-Link auf nicht wählbaren Einsatz)
            removeStored(keyRef.current)
            applyDirect(null, 'ungueltig', false)
            if (originRef.current === 'deeplink') {
              setHinweis({ art: 'nicht-verfuegbar', id: cur, titel })
              suggestDoneRef.current = true // kein stiller Ersatz für einen ausdrücklich angeforderten Einsatz
            }
          }
        }
      }
      await trySuggest()
    }
    const clear = () => { inFlight.current = null }
    const pr = run().then(clear, clear)
    inFlight.current = pr
    return pr
  }, [applyDirect, trySuggest])

  // ---- Wechsel mit Guards
  const commit = useCallback(async (next: number | null, grund: EinsatzGrund): Promise<boolean> => {
    const von = idRef.current
    if (next === von) return true
    if (next !== null && loadedRef.current && !listRef.current.some(x => x.id === next)) {
      setHinweis({ art: 'nicht-verfuegbar', id: next, titel: titelRef.current.get(next) ?? `Einsatz ${next}` })
      return false
    }
    const token = ++changeToken.current
    emit('einsatz:changing', { von, nach: next, grund })
    for (const g of Array.from(guards.current)) {
      try {
        if ((await g(next, von, grund)) === false) return false
      } catch {
        return false
      }
    }
    if (token !== changeToken.current || idRef.current !== von) return false // von neuerer Wahl überholt
    originRef.current = 'user'
    userNoneRef.current = next === null
    suggestDoneRef.current = true
    if (next !== null) seenRef.current = listRef.current.some(x => x.id === next)
    setHinweis(null)
    applyDirect(next, grund, true)
    return true
  }, [applyDirect])

  const setEinsatz = useCallback(async (id: number | string | null) => {
    if (props.current.locked) return false
    const parsed = id === null || id === '' ? null : parseEinsatzId(id)
    if (parsed === null && id !== null && id !== '') return false
    if (parsed === null && !allowNone) return false
    const ok = await commit(parsed, 'user')
    // Multimonitor: weitere Fenster desselben Moduls (und Nutzers) folgen sofort
    if (ok) fensterKanal('einsatz')?.postMessage({ key: keyRef.current, id: parsed, von: FENSTER_ID })
    return ok
  }, [commit, allowNone])

  // ---- Multimonitor: Wahl aus einem anderen Fenster übernehmen (läuft durch die Guards wie eine eigene Wahl)
  useEffect(() => {
    const k = fensterKanal('einsatz')
    if (!k) return
    const on = (e: MessageEvent) => {
      const m = e.data as { key?: string; id?: number | null; von?: string } | null
      if (!m || m.von === FENSTER_ID || m.key !== keyRef.current || props.current.locked) return
      const id = m.id === null ? null : parseEinsatzId(m.id)
      if (id === idRef.current || (id !== null && !listRef.current.some(x => x.id === id))) return
      if (id === null && !allowNone) return
      void commit(id, 'user')
    }
    k.addEventListener('message', on)
    return () => k.removeEventListener('message', on)
  }, [commit, allowNone])

  // ---- Deep-Link ?einsatz=<id> hat Vorrang und wird (standardmäßig) danach aus der URL entfernt
  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    const raw = sp.get('einsatz')
    if (raw === null) return
    const id = parseEinsatzId(raw)
    const done = () => {
      if (keepParam) return
      sp.delete('einsatz')
      const search = sp.toString()
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true, state: location.state })
    }
    if (id === null || id === idRef.current) { done(); return }
    originRef.current = 'deeplink'
    void commit(id, 'deeplink').then(done, done)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // ---- Nutzerwechsel (anderer Speicherschlüssel): Zustand neu aufbauen
  const firstKey = useRef(true)
  useEffect(() => {
    if (firstKey.current) { firstKey.current = false; return }
    const st = readStored(key)
    suggestDoneRef.current = false
    seenRef.current = false
    originRef.current = st ? 'stored' : 'suggest'
    userNoneRef.current = !!st && st.id === null
    setHinweis(null)
    applyDirect(st?.id ?? null, 'ungueltig', false)
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // ---- Laden, Polling (nur sichtbar), Wiederkehr, "Erneut versuchen"
  useEffect(() => {
    void reload()
    const tick = () => { if (document.visibilityState !== 'hidden') void reload() }
    const t = pollMs > 0 ? setInterval(tick, pollMs) : null
    const onVis = () => { if (document.visibilityState === 'visible') void reload() }
    const onRetry = () => { void reload() }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('connection:retry', onRetry)
    return () => {
      if (t) clearInterval(t)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('connection:retry', onRetry)
    }
  }, [pollMs, reload, endpoint])

  // 'mission'-Vorschlag: sobald der MissionProvider fertig geladen hat
  const missionLoading = mission?.loading
  useEffect(() => { if (missionLoading === false) void trySuggest() }, [missionLoading, mission?.activeMission?.id, trySuggest])

  const guardApi = useMemo(() => ({
    add: (g: EinsatzWechselGuard) => { guards.current.add(g); return () => { guards.current.delete(g) } },
  }), [])

  const einsatz = useMemo(() => einsaetze.find(x => x.id === einsatzId) ?? null, [einsaetze, einsatzId])
  const value = useMemo<EinsatzContextValue>(() => ({
    einsaetze, einsatz, einsatzId, einsatzIdStr: einsatzId === null ? '' : String(einsatzId),
    loading, fehler, hinweis, dismissHinweis: () => setHinweis(null),
    setEinsatz, reload, locked, lockedHint, allowNone, modul,
  }), [einsaetze, einsatz, einsatzId, loading, fehler, hinweis, setEinsatz, reload, locked, lockedHint, allowNone, modul])

  return (
    <GuardCtx.Provider value={guardApi}>
      <Ctx.Provider value={value}>{children}</Ctx.Provider>
    </GuardCtx.Provider>
  )
}

/** Globale Einsatzwahl. Nur innerhalb des EinsatzProviders. */
export function useEinsatz(): EinsatzContextValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useEinsatz nur innerhalb des EinsatzProviders verwenden')
  return v
}

/** Wie useEinsatz, liefert aber null ohne Provider (z. B. Leitstelle/Admin ohne Einsatzwahl). */
export function useOptionalEinsatz(): EinsatzContextValue | null {
  return useContext(Ctx)
}

/**
 * Seiten mit Auto-Save/ungespeicherten Änderungen melden sich hier an. Der Guard läuft VOR jedem Wechsel
 * (auch Deep-Link); er speichert und gibt nichts/true zurück oder gibt false zurück, um den Wechsel abzubrechen:
 *   useEinsatzWechsel(async () => { await speichereJetzt() })           // speichern, dann wechseln
 *   useEinsatzWechsel(() => !dirty || bestaetigen({ titel: "Verwerfen?", ... }))  // ggf. abbrechen (useBestaetigung)
 * Der Guard sieht immer die aktuelle Fassung (keine veralteten Closures).
 */
export function useEinsatzWechsel(guard: EinsatzWechselGuard) {
  const g = useContext(GuardCtx)
  const ref = useRef(guard)
  ref.current = guard
  useEffect(() => {
    if (!g) return
    return g.add((nach, von, grund) => ref.current(nach, von, grund))
  }, [g])
}
